import React, {
  createContext,
  ForwardedRef,
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { useBlocker } from "react-router-dom";
import NavigationStepperCst from "@components/NavigationStepperCst";
import { WizardCstProps, WizardHandle } from "./types";
import { useWizardState } from "./hooks/useWizardState";
import {
  WizardContainer,
  WizardStepperRow,
  WizardContent,
  WizardReadOnlyAreaRoot,
} from "./style";
import WizardHeader from "./components/WizardHeader";
import WizardFooter from "./components/WizardFooter";
import WizardResetDialog from "./components/WizardResetDialog";
import WizardAbandonDialog from "./components/WizardAbandonDialog";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";

const WizardReadOnlyContext = createContext(false);

export const useWizardReadOnly = () => useContext(WizardReadOnlyContext);

const ACTION_SELECTOR = 'button, [role="button"], a[href]';
const ALLOWED_SELECTOR = '.MuiAccordionSummary-root, [role="tab"]';
const TEXT_FIELDS_SELECTOR = "input, textarea";

const isBlockedTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false;
  const action = target.closest(ACTION_SELECTOR);
  return !!action && !action.closest(ALLOWED_SELECTOR);
};

const blockAction = (e: React.SyntheticEvent) => {
  if (!isBlockedTarget(e.target)) return;
  e.preventDefault();
  e.stopPropagation();
};

const blockActionKeys = (e: React.KeyboardEvent) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  blockAction(e);
};

const setTextFieldsReadOnly = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      TEXT_FIELDS_SELECTOR,
    )
    .forEach((el) => {
      if (!el.readOnly) el.readOnly = true;
    });
};

export const WizardReadOnlyArea: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    setTextFieldsReadOnly(root);
    const observer = new MutationObserver(() => setTextFieldsReadOnly(root));
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <WizardReadOnlyAreaRoot
      ref={ref}
      onClickCapture={blockAction}
      onMouseDownCapture={blockAction}
      onKeyDownCapture={blockActionKeys}
    >
      {children}
    </WizardReadOnlyAreaRoot>
  );
};

function WizardCstInner<TData>(
  {
    title,
    breadcrumb,
    backLink,
    headerAction,
    renderHeaderBadge,
    steps,
    initialData,
    onComplete,
    resetConfirm,
    abandonMode,
    abandonCopy,
    nextLabel = "Prosegui",
    resetLabel = "Reset",
    completeLabel = "Crea",
    backLabel = "Indietro",
    secondaryAction,
    isCompleting,
    stickyHeader = true,
    readOnly = false,
    navigationMode = "sequential",
  }: WizardCstProps<TData>,
  ref: ForwardedRef<WizardHandle<TData>>,
) {
  const {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goNext,
    goBack,
    goToStep,
    navigateToStep,
    validateAll,
    isStepCompleted,
    isStepReachable,
    reset,
    isFirstStep,
    isLastStep,
    isDirty,
  } = useWizardState({ steps, initialData, navigationMode });

  const [isPersisted, setIsPersisted] = useState(false);
  const isPersistedRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef(false);

  const stepSetData = useMemo<typeof setData>(
    () =>
      readOnly
        ? () => {
            /* sola lettura */
          }
        : setData,
    [readOnly, setData],
  );

  const shouldBlock = !readOnly && !!abandonMode && isDirty && !isPersisted;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isPersistedRef.current &&
      shouldBlock &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") setAbandonDialogOpen(true);
  }, [blocker.state]);

  useImperativeHandle(ref, () => ({ setData: stepSetData }), [stepSetData]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
    let el = containerRef.current?.parentElement ?? null;
    while (el) {
      if (el.scrollTop > 0) el.scrollTop = 0;
      el = el.parentElement;
    }
  }, [activeStep]);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleNext = async () => {
    if (isCompleting) return;
    if (readOnly) {
      goNext();
      return;
    }
    if (isLastStep) {
      if (!validateAll()) return;
      try {
        isPersistedRef.current = true;
        setIsPersisted(true);
        await onComplete(data);
      } catch {
        isPersistedRef.current = false;
        setIsPersisted(false);
      }
      return;
    }
    tryNext();
  };

  const handleResetClick = () => {
    if (!resetConfirm) {
      reset();
      return;
    }
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    reset();
  };

  const runSecondaryAction = async () => {
    if (!secondaryAction) return;
    isPersistedRef.current = true;
    try {
      await secondaryAction.onClick(data);
      setIsPersisted(true);
    } catch {
      isPersistedRef.current = false;
    }
  };

  const ActiveStepComponent = steps[activeStep]?.Component;
  const nextDisabled = steps[activeStep]?.canProceed?.(data) === false;
  const secondaryDisabled =
    typeof secondaryAction?.disabled === "function"
      ? secondaryAction.disabled(data)
      : !!secondaryAction?.disabled;
  const visibleStepIndexes = steps.reduce<number[]>((acc, s, index) => {
    if (!(s.hideWhenSkipped && s.skip?.(data))) acc.push(index);
    return acc;
  }, []);
  const stepperSteps = visibleStepIndexes.map((index) => ({
    key: steps[index].key,
    label: steps[index].label,
  }));
  const stepperActiveStep = visibleStepIndexes.indexOf(activeStep);
  const stepperCompletedSteps = visibleStepIndexes.reduce<number[]>(
    (acc, index, visibleIndex) => {
      if (isStepCompleted(index)) acc.push(visibleIndex);
      return acc;
    },
    [],
  );

  const headerBlock = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <WizardHeader
        title={title}
        breadcrumb={breadcrumb}
        backLink={backLink}
        headerAction={headerAction}
        titleBadge={renderHeaderBadge?.(data)}
      />

      <WizardStepperRow>
        <NavigationStepperCst
          steps={stepperSteps}
          activeStep={stepperActiveStep}
          onStepChange={(visibleIndex) => {
            const index = visibleStepIndexes[visibleIndex];
            if (readOnly) goToStep(index);
            else navigateToStep(index);
          }}
          allowBackNavigation
          allowForwardNavigation={readOnly}
          completedSteps={readOnly ? undefined : stepperCompletedSteps}
          isStepClickable={
            readOnly
              ? undefined
              : (visibleIndex) =>
                  isStepReachable(visibleStepIndexes[visibleIndex])
          }
        />
      </WizardStepperRow>
    </Box>
  );

  return (
    <WizardContainer ref={containerRef}>
      {stickyHeader ? (
        <PageHeaderV2Cst>{headerBlock}</PageHeaderV2Cst>
      ) : (
        headerBlock
      )}

      <WizardContent>
        {ActiveStepComponent && (
          <WizardReadOnlyContext.Provider value={readOnly}>
            {readOnly ? (
              <WizardReadOnlyArea>
                <ActiveStepComponent
                  data={data}
                  setData={stepSetData}
                  errors={errors}
                  mode={abandonMode}
                />
              </WizardReadOnlyArea>
            ) : (
              <ActiveStepComponent
                data={data}
                setData={stepSetData}
                errors={errors}
                mode={abandonMode}
              />
            )}
          </WizardReadOnlyContext.Provider>
        )}
      </WizardContent>

      <WizardFooter
        resetLabel={resetLabel}
        nextLabel={isLastStep ? completeLabel : nextLabel}
        backLabel={backLabel}
        onReset={handleResetClick}
        onNext={handleNext}
        onBack={goBack}
        showBack={!isFirstStep}
        showReset={!readOnly}
        showNext={!(readOnly && isLastStep)}
        nextDisabled={!readOnly && nextDisabled}
        secondaryAction={
          secondaryAction && !readOnly
            ? {
                label: secondaryAction.label,
                onClick: () => void runSecondaryAction(),
                disabled: secondaryDisabled,
              }
            : undefined
        }
        isCompleting={isCompleting}
      />

      {resetConfirm && (
        <WizardResetDialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          onConfirm={handleResetConfirm}
          copy={resetConfirm}
        />
      )}

      {abandonMode && !readOnly && (
        <WizardAbandonDialog
          open={abandonDialogOpen}
          mode={abandonMode}
          copy={abandonCopy}
          onClose={() => {
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.reset();
          }}
          onAbandon={() => {
            isPersistedRef.current = true;
            setAbandonDialogOpen(false);
            if (blocker.state === "blocked") blocker.proceed();
          }}
          onSaveDraft={
            secondaryAction && !secondaryDisabled
              ? () => {
                  setAbandonDialogOpen(false);
                  if (blocker.state === "blocked") blocker.reset();
                  void runSecondaryAction();
                }
              : undefined
          }
        />
      )}
    </WizardContainer>
  );
}

const WizardCst = forwardRef(WizardCstInner) as <TData>(
  props: WizardCstProps<TData> & { ref?: Ref<WizardHandle<TData>> },
) => ReturnType<typeof WizardCstInner>;

export default WizardCst;
